import React from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { Card, Flex, LoadingIndicator, Tag, Text, type TagVariant } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import type { IngestionState } from "#graphql/supergraphTypes";

export type IngestionCardData = {
  id: string;
  state: IngestionState;
  receivedAt: string;
  documentCount: number;
  classifiedCount: number;
};

// COMPLETED is intentionally absent from both maps: an approved batch drops off
// the rail (see UploadRail's visibleIngestions filter), so only CLASSIFYING and
// AWAITING_CONFIRMATION cards ever render here. The `?? "neutral"` /
// `?? ingestion.state` fallbacks are what a COMPLETED card would land on if one
// ever leaked through, rather than throwing.
const STATE_TAG_VARIANT: Partial<Record<IngestionState, TagVariant>> = {
  CLASSIFYING: "informative",
  AWAITING_CONFIRMATION: "warning",
};

const STATE_LABEL: Partial<Record<IngestionState, string>> = {
  CLASSIFYING: "Classifying",
  AWAITING_CONFIRMATION: "Ready for review",
};

/** Tighter than `Sizes.SM` (8), which leaves the spinner floating away from the
 *  count it belongs to, and wider than `Sizes.XS` (4), which crowds them into
 *  one glyph. */
const SPINNER_COUNT_GAP = 6;

/** Strict relative time ("12 minutes ago", "3 hours ago") — drops the vague
 *  "about" prefix that formatDistanceToNow adds, keeping the title compact. */
function formatReceived(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return formatDistanceToNowStrict(date, { addSuffix: true });
}

export type BatchCardProps = {
  ingestion: IngestionCardData;
  onSelect?: (id: string) => void;
};

/**
 * BatchCard — a single ingestion in the Incoming Documents rail.
 *
 * This renders state-only: the live per-file upload progress and percentage bar
 * are punted, so we show the coarse state chip plus a "M of N classified" count
 * and a spinner while classification is in flight. A classifying batch is inert
 * — there's nothing to review yet, so opening the slideout would only strand on
 * a "Loading workflow…" tree. An awaiting-review batch opens the confirm
 * screen. Completed batches drop off the rail entirely (UploadRail filters them
 * out), so a COMPLETED card never renders here.
 */
export const BatchCard: React.FC<BatchCardProps> = ({ ingestion, onSelect }) => {
  const isClassifying = ingestion.state === "CLASSIFYING";
  const fileNoun = ingestion.documentCount === 1 ? "file" : "files";

  return (
    <Card
      isFullWidth
      padding="md"
      isInteractive={!isClassifying}
      role={isClassifying ? undefined : "button"}
      tabIndex={isClassifying ? undefined : 0}
      onClick={isClassifying ? undefined : () => onSelect?.(ingestion.id)}
      onKeyDown={
        isClassifying
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect?.(ingestion.id);
              }
            }
      }
    >
      <Flex flexDirection="column" gap={Sizes.SM}>
        <Flex justifyContent="space-between" alignItems="baseline" gap={Sizes.SM}>
          <Text variant="strong-md">
            {ingestion.documentCount} {fileNoun} · {formatReceived(ingestion.receivedAt)}
          </Text>
          <Tag
            variant={STATE_TAG_VARIANT[ingestion.state] ?? "neutral"}
            label={STATE_LABEL[ingestion.state] ?? ingestion.state}
          />
        </Flex>
        <Flex inline alignItems="center" gap={SPINNER_COUNT_GAP}>
          {isClassifying && <LoadingIndicator size="small" variant="primary" />}
          <Text variant="secondary-sm" asSpan>
            {ingestion.classifiedCount} of {ingestion.documentCount} classified
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
};
